import React, { type ReactNode, type Ref, useCallback } from 'react';
import {
  type GestureResponderEvent,
  StyleSheet,
  TouchableOpacity,
  type TouchableOpacityProps,
  type View,
} from 'react-native';
import Animated from 'react-native-reanimated';

import { theme } from '@garnish/constants';

import { GlassView } from '../../../GlassView';
import { BodyText } from '../../../Text';
import { useNavbarPressScale } from '../../hooks/useNavbarPressScale';
import { NAVBAR_CONTENT_HEIGHT } from '../../Navbar.constants';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarButton = (props: Props) => {
  const {
    accessibilityRole = 'button',
    activeOpacity = NAVBAR_BUTTON_ACTIVE_OPACITY,
    children,
    isLiquidGlassEnabled = false,
    onPressIn,
    onPressOut,
    ref,
    style,
    title,
    ...restProps
  } = props;

  const { handlePressIn, pressScaleAnimatedStyles } = useNavbarPressScale();

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleButtonPressIn = useCallback(
    (event: GestureResponderEvent) => {
      if (isLiquidGlassEnabled) handlePressIn();

      onPressIn?.(event);
    },
    [handlePressIn, isLiquidGlassEnabled, onPressIn],
  );

  // ─── Styles ──────────────────────────────────────────────────────────

  const buttonStyles = [
    styles.button,
    isLiquidGlassEnabled ? undefined : styles.buttonSolid,
    isLiquidGlassEnabled ? pressScaleAnimatedStyles : undefined,
    style,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <AnimatedTouchableOpacity
      ref={ref}
      accessibilityRole={accessibilityRole}
      activeOpacity={activeOpacity}
      style={buttonStyles}
      onPressIn={handleButtonPressIn}
      onPressOut={onPressOut}
      {...restProps}
    >
      {isLiquidGlassEnabled ? (
        <GlassView
          tintColor={NAVBAR_BUTTON_GLASS_TINT_COLOR}
          glassEffectStyle="clear"
          style={styles.glass}
        />
      ) : null}

      <BodyText
        maxFontSizeMultiplier={1.2}
        numberOfLines={1}
        sizeMatch={['16']}
        style={styles.title}
      >
        {title}
      </BodyText>

      {children}
    </AnimatedTouchableOpacity>
  );
};

// ─── Animated Components ─────────────────────────────────────────────────────

const AnimatedTouchableOpacity = Animated.createAnimatedComponent(TouchableOpacity);

// ─── Constants ───────────────────────────────────────────────────────────────

const NAVBAR_BUTTON_MAX_WIDTH = 100;
const NAVBAR_BUTTON_ACTIVE_OPACITY = 0.9;

const NAVBAR_BUTTON_BORDER_RADIUS = NAVBAR_CONTENT_HEIGHT / 2;
const NAVBAR_BUTTON_GLASS_TINT_COLOR = 'rgba(0, 71, 60, 0.99)';

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  button: {
    width: '100%',
    maxWidth: NAVBAR_BUTTON_MAX_WIDTH,
    height: NAVBAR_CONTENT_HEIGHT,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: NAVBAR_BUTTON_BORDER_RADIUS,
    borderCurve: 'continuous',
    paddingHorizontal: theme.spacing['4'],
    boxShadow: '0px 0px 20px 0px rgba(14, 21, 14, 0.05)',
  },
  buttonSolid: {
    backgroundColor: theme.colors.KALE,
  },
  glass: {
    ...StyleSheet.absoluteFill,
    borderRadius: NAVBAR_BUTTON_BORDER_RADIUS,
    borderCurve: 'continuous',
    pointerEvents: 'none',
  },
  title: {
    color: theme.colors.WHITE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = Omit<TouchableOpacityProps, 'children'> & {
  children?: ReactNode;
  isLiquidGlassEnabled?: boolean;
  ref?: Ref<View>;
  title: ReactNode;
};
